import untypedGroupBy from 'lodash/groupBy'

import { isNumberInRange } from './number';
import type { NonMutable, Nullable } from './object';
import { compareByOrder } from './sortComparators';

export const nullIfEmpty = <T extends string | readonly unknown[]>(value: T): Nullable<T> => (
  value.length === 0 ? null : value
);

export const append = <Item>(arg: Item) => (arr: Item[]): Item[] => arr.concat([arg]);
export const drop = <Item>(arg: Item) => (arr: Item[]): Item[] => arr
  .filter((item) => item !== arg);

export const join = <Item>(arg: Item[]) => (arr: Item[]): Item[] => arr.concat(arg);
export const remove = <Item>(arg: Item[]) => (arr: Item[]): Item[] => arr.filter((item) => !arg.includes(item));

export const insertAtIndex = <Item>(arg: Item, index: number, arr: NonMutable<Item[]>): Item[] => [
  ...arr.slice(0, index),
  arg,
  ...arr.slice(index),
];

export const moveItem = <Item>(list: NonMutable<Item[]>, startIndex: number, endIndex: number): NonMutable<Item[]> => {
  if (!isNumberInRange(startIndex, 0, list.length - 1)
    || !isNumberInRange(endIndex, 0, list.length - 1)) {

    return list;
  }

  const copy = [...list];
  const [removed] = copy.splice(startIndex, 1);
  copy.splice(endIndex, 0, removed);

  return copy;
};

export const joinedFilters = <Item>(
  arg: ((arg: Item) => boolean)[],
): (arg: Item) => boolean => (item) => arg.every((filter) => filter(item));

export const replaceAtIndex = <Item>(arg: Item, index: number, arr: NonMutable<Item[]>): Item[] => arr
  .map((el, i) => (i === index ? arg : el));

export const sortByOrder = <OrderItem, ArrayItem>(
  arg: NonMutable<ArrayItem[]>, order: NonMutable<OrderItem[]>, extract: (item: ArrayItem) => OrderItem,
): ArrayItem[] => {
  const compare = compareByOrder(order);

  return arg.toSorted((itemA, itemB) => compare(extract(itemA), extract(itemB)));
};

export const includes = <ArrayItem>(
  arg: NonMutable<ArrayItem[]>,
  potentialItem: unknown
): potentialItem is ArrayItem => arg.some((item) => item === potentialItem)

export const reduceWhile = <Input, Output>(
  array: NonMutable<Input[]>,
  iterator: (acc: Output, current: Input, index: number, array: NonMutable<Input[]>) => Output,
  defaultValue: Output,
  breakWhen: (acc: Output, current: Input, index: number, array: NonMutable<Input[]>) => boolean,
): Output => {
  let output = defaultValue;

  for (let i = 0; i < array.length; i++) {
    output = iterator(output, array[i], i, array);

    if (breakWhen(output, array[i], i, array)) {
      return output;
    }
  }

  return output;
}

export const reduce = <Input, Output>(
  array: NonMutable<Input[]>,
  iterator: (acc: Output, current: Input, index: number, array: NonMutable<Input[]>) => Output,
  defaultValue: Output,
): Output => reduceWhile(array, iterator, defaultValue, () => false)

export const groupBy = <T, U extends string>(
  array: NonMutable<T[]>, fn: (el: T) => U
): Partial<Record<U, T[]>> => untypedGroupBy(array, fn) as unknown as Partial<Record<U, T[]>>

export const areEqual = (a: NonMutable<unknown[]>, b: NonMutable<unknown[]>): boolean => a.length === b.length
  && a.every((el, index) => el === b[index])

export const findIndexFromIndex = <T>(
  array: NonMutable<T[]>,
  matchFn: (el: T) => boolean,
  startIndex: number
) => {
  const indexLimit = Math.min(
    array.length,
    Math.max(array.length - startIndex, startIndex)
  )

  for (let i = 0; i < indexLimit; i++) {
    const leftIndex = startIndex - i;
    const rightIndex = startIndex + i;

    const leftEl = array[leftIndex];
    const rightEl = array[rightIndex];

    if (leftIndex >= 0 && matchFn(leftEl)) {
      return startIndex - i;
    }

    if (rightIndex < array.length && matchFn(rightEl)) {
      return rightIndex
    }
  }

  return -1;
}
